import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  linkTextClasses,
  Panel,
  StatCard,
  StatCardList,
  type IconName,
} from '@evogard/ui';
import type { BillingNumbers, BoletoTotal } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
/*
 * As duas vêm de `common.tsx`, que é onde mora o vocabulário que as telas deste
 * módulo dividem: `MoneyValue` porque duas formas de escrever real na mesma
 * casa é o que este módulo desfaz — inclusive o `catch` que transforma um valor
 * fora de forma em "valor ilegível" em vez de derrubar a página —, e `Age`
 * porque o painel e a esteira dizem a mesma frase sobre o mesmo fato.
 *
 * `Age` nasceu dentro de `PipelineScreen.tsx` e o painel a importava de lá. Uma
 * tela importando componente de outra compila e passa no lint, e é assim que o
 * painel do início passa a depender do arquivo da esteira — quem apagasse uma
 * seção daquela tela levaria o painel junto, sem nada apontar para cá.
 */
import { Age, MoneyValue } from './common.js';

/**
 * Os números de cobrança no início da interna — `cobranca-27`.
 *
 * ## Pelo contrato DESTE módulo, e não por um relatório lendo tabela alheia
 *
 * `GET /billing/numbers` é rota da cobrança, sob `billing.desk:ver` — a mesma
 * permissão da mesa, porque a contagem é a mesma informação agregada. É a
 * decisão que evita o `DashboardDiretoria` renascer com outro nome: cada módulo
 * publica os próprios números, e o início é uma moldura que os reúne.
 *
 * ## A união discriminada é o que este painel existe para respeitar
 *
 * `sem_coleta` não é zero. O espelho do ERP está construído e vazio nas duas
 * praças, então a coleta nunca rodou — e "R$ 0,00 em aberto" seria a casa
 * afirmando, na primeira tela que alguém abre de manhã, que ninguém deve nada.
 * É o número mais caro que uma tela inicial pode inventar, e o painel escreve o
 * que sabe em vez dele.
 *
 * `porEtapa` tem união própria pelo mesmo motivo, um nível abaixo: as três
 * medidas de cima existem com ou sem régua, e a quebra por etapa não. Com uma
 * lista lisa, `etapas: []` diria ao mesmo tempo "esta praça não configurou a
 * régua" e "a régua existe e nenhuma etapa pegou ninguém" — a segunda é uma
 * resposta sobre a operação, a primeira é uma configuração que falta.
 *
 * ## E o número nunca é de agora
 *
 * Ele é o retrato da coleta que o produziu, e o painel diz de quando. Sem isso,
 * o valor em aberto de três dias atrás aparece no início da manhã com cara de
 * saldo do dia.
 */
export function BillingPanel() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  const query = useQuery({
    queryKey: ['cobranca', 'numeros', tenantId],
    queryFn: async () => {
      const r = await api.billing.numbers();
      return ok(r, 200, 'Não foi possível carregar os números.');
    },
    /*
     * Segura o resultado anterior só dentro da mesma base: os valores em aberto
     * da CE sob o nome da GO seriam uma afirmação sobre dinheiro de outra praça.
     */
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const n = query.data;

  return (
    <Panel
      title="Cobrança"
      descricao="O que está em aberto nesta base."
      actions={
        /*
         * `linkTextClasses` e não a string escrita à mão: um `<a>` solto tem a
         * altura da linha — 17px — e é alvo de dedo como qualquer outro. Este
         * arquivo foi o primeiro a corrigir, e o comentário aqui dizia que as
         * outras quatro ficavam "para quem for tocar naqueles arquivos". Em
         * 01/09/2026 as nove foram convertidas de uma vez e a régua do design
         * passou a derrubar a décima na build.
         */
        <Link to="/billing/desk" className={linkTextClasses('text-xs')}>
          Ver a mesa
        </Link>
      }
      /*
       * A quebra por etapa e a data da coleta vão para o RODAPÉ do painel, e
       * cada uma perdeu o `border-t` que escrevia sozinha: com a linha do
       * `Panel` acima delas, a tela desenhava três separadores empilhados sob os
       * mesmos três cartões.
       */
      footer={
        n?.estado === 'medido' ? (
          <>
            <ByStep byStep={n.byStep} />
            <p className="mt-3 text-xs text-texto-suave">
              Retrato da coleta de <Age iso={n.collectedAt} />. Vencidos conta pelo
              vencimento original, não pelo corrente: prorrogar um boleto não tira ninguém
              da inadimplência.
            </p>
          </>
        ) : undefined
      }
    >
      {query.isError ? (
        <p className="text-sm text-texto-suave">
          Não foi possível ler os números agora. A mesa continua acessível.
        </p>
      ) : n?.estado === 'sem_coleta' ? (
        /*
         * Não é "R$ 0,00 em aberto". É o estoque que nunca foi montado — e é o
         * estado das duas praças enquanto o espelho do ERP estiver vazio.
         */
        <WithoutIntake />
      ) : (
        <StatCardList>
          <BoletoStat icon="cobranca" label="Em aberto" total={n?.atOpen} />
          <BoletoStat
            icon="relogio"
            label="Vencidos"
            total={n?.overdue}
            tone={n?.estado === 'medido' && n.overdue.amount > 0 ? 'erro' : undefined}
          />
          <BoletoStat
            icon="dinheiro"
            label="Recebido no mês"
            total={n?.receivedNoMonth}
          />
        </StatCardList>
      )}
    </Panel>
  );
}

/**
 * A coleta nunca rodou — e o painel diz o que isso quer dizer.
 *
 * Nada de "nada aqui": a frase ensina qual é o estado e o que o destrava, que é
 * a carga do espelho do ERP. Sem isso, quem lê conclui que a base está em dia.
 */
function WithoutIntake() {
  return (
    <p className="text-sm">
      A coleta de boletos nunca rodou nesta base, então não há o que contar — e escrever
      “R$ 0,00 em aberto” aqui seria afirmar que ninguém deve nada. A coleta monta o
      estoque a partir do espelho do ERP, que ainda não foi preenchido nesta praça.{' '}
      <Link to="/billing/pipeline" className={linkTextClasses()}>
        Esteira de cobrança
      </Link>{' '}
      mostra o estado da coleta e da régua.
    </p>
  );
}

/**
 * Um cartão de medida: o dinheiro em cima, a contagem embaixo.
 *
 * Enquanto a consulta está no ar, o lugar dos dois é esqueleto e não zero —
 * zero é um fato, e afirmá-lo antes de saber é a tela mentindo com aparência
 * de precisão.
 *
 * O dinheiro atravessa a rede como **texto**, na forma da coluna
 * `numeric(12,2)`. A conversão é `fromColumn` de `@evogard/domain`, que devolve
 * centavo inteiro, e a formatação é `format`: `Number(valor) * 100` funciona
 * em todo teste e perde o centavo na conciliação do mês.
 */
function BoletoStat({
  icon,
  label,
  total,
  tone,
}: {
  icon: IconName;
  label: string;
  total: BoletoTotal | undefined;
  tone?: 'erro';
}) {
  return (
    <StatCard
      icon={icon}
      label={label}
      tone={tone}
      value={total === undefined ? undefined : <MoneyValue value={total.value} />}
      /*
       * String vazia, e não `undefined`, enquanto a consulta está no ar: é
       * assim que o `StatCard` sabe que este número TEM linha de apoio e que o
       * esqueleto precisa das duas barras. Com `undefined` o esqueleto perderia
       * a segunda e a altura do cartão mudaria quando o número chegasse.
       */
      support={
        total === undefined
          ? ''
          : `${total.amount} ${total.amount === 1 ? 'boleto' : 'boletos'}`
      }
    />
  );
}

/**
 * A quebra por etapa, e o ramo que roda hoje.
 *
 * `sem_regua` não desenha lista vazia: sem faixa não há etapa em que um boleto
 * caiba, e o que a pessoa precisa saber é que a cobrança automática desta praça
 * não está desligada por acaso — ela nunca foi configurada.
 *
 * No ramo medido, as etapas zeradas aparecem: a fila que ninguém está enchendo
 * é informação, e escondê-la faria este painel divergir da esteira que ele
 * resume.
 */
function ByStep({
  byStep,
}: {
  byStep: Extract<BillingNumbers, { estado: 'medido' }>['byStep'];
}) {
  if (byStep.estado === 'sem_regua') {
    return (
      <p className="text-sm">
        Esta base não tem régua de cobrança, então não há etapa para quebrar os números
        acima — e a esteira não dispara nada.{' '}
        <Link to="/billing/pipeline" className={linkTextClasses()}>
          Esteira de cobrança
        </Link>{' '}
        diz o que falta.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-1.5">
      {byStep.steps.map((e) => (
        <li key={e.step} className="flex items-baseline justify-between gap-3 text-sm">
          <span className="min-w-0 truncate">{e.step}</span>
          <span className="shrink-0 tabular-nums text-texto-suave">
            {e.amount}
            <span className="ml-2 text-texto">
              <MoneyValue value={e.value} />
            </span>
          </span>
        </li>
      ))}
      {/*
       * Fora da régua fica na mesma lista, e não escondido: é o inadimplente que
       * a cobrança automática não alcança. Some quando é zero — uma linha
       * permanente escrita "0" gastaria a atenção que ela precisa comprar
       * justamente no dia em que deixar de ser zero.
       */}
      {byStep.ladderOutside.amount > 0 && (
        <li className="flex items-baseline justify-between gap-3 border-t border-borda pt-1.5 text-sm">
          <span className="min-w-0 truncate font-medium text-alerta">Fora da régua</span>
          <span className="shrink-0 tabular-nums text-texto-suave">
            {byStep.ladderOutside.amount}
            <span className="ml-2 text-texto">
              <MoneyValue value={byStep.ladderOutside.value} />
            </span>
          </span>
        </li>
      )}
    </ul>
  );
}
